// Черновики незаконченного ввода.
//
// iOS выгружает PWA из памяти, когда его свернули надолго, а любая
// перезагрузка страницы (новая сборка, ошибка чанка) стартует приложение с
// нуля — наполовину набранный приём или карточка продукта пропадали. Окна
// пишут своё состояние сюда по мере ввода, а дневник при старте предлагает
// продолжить (Diary.vue → AddFoodDialog с restore).
//
// Текст — в localStorage под ключом zoya-draft:<kind>, фото — в IndexedDB
// (в localStorage оно не влезет: квота 5 МБ на всё). Виды:
//   meal    — окно распознавания (фото, голос, текст, повтор; не правка)
//   product — ручное добавление продукта из базы (выбран продукт и вес)
//   label   — карточка нового продукта (форма этикетки)
// Черновик живёт сутки: старше — скорее мусор, чем забытый обед.

const PREFIX = 'zoya-draft:'
const MAX_AGE_MS = 24 * 3600 * 1000

export function saveDraft(kind, data) {
  try {
    localStorage.setItem(PREFIX + kind, JSON.stringify({ ...data, savedAt: Date.now() }))
  } catch {
    // квота или приватный режим — черновик не обязателен
  }
}

export function loadDraft(kind, maxAgeMs = MAX_AGE_MS) {
  try {
    const raw = localStorage.getItem(PREFIX + kind)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (!d || !d.savedAt || Date.now() - d.savedAt > maxAgeMs) {
      localStorage.removeItem(PREFIX + kind)
      return null
    }
    return d
  } catch {
    return null
  }
}

export function clearDraft(kind) {
  try { localStorage.removeItem(PREFIX + kind) } catch { /* ignore */ }
  delPhoto(kind).catch(() => {})
}

// Какие черновики есть, от важного к менее важному: приём с распознаванием,
// карточка продукта, продукт из базы. Дневник предлагает первый.
export function pendingDrafts() {
  const out = []
  for (const kind of ['meal', 'label', 'product']) {
    const d = loadDraft(kind)
    if (d) out.push({ kind, ...d })
  }
  return out
}

// Заголовок для вопроса «Вы не закончили добавлять «…». Продолжить?»
export function draftTitle(draft) {
  if (!draft) return ''
  if (draft.kind === 'meal') {
    if (draft.dishName) return draft.dishName
    const first = draft.items?.[0]?.name
    const n = draft.items?.length || 0
    if (first && n > 1) return `${first} и ещё ${n - 1}`
    return first || 'приём пищи'
  }
  if (draft.kind === 'product') return draft.product?.name || 'продукт'
  if (draft.kind === 'label') return draft.reviewData?.name || 'новый продукт'
  return ''
}

// Сериализуемая копия позиций окна распознавания: функции и DOM в черновик
// не лезут, остальное копируется как есть (per100, раскладки, client_id —
// по нему сервер узнает повтор и не создаст дубль, даже если первый
// ответ потерялся за перезагрузкой).
export function serializeItems(items) {
  try {
    return JSON.parse(JSON.stringify(items, (k, v) => (typeof v === 'function' ? undefined : v)))
  } catch {
    return []
  }
}

// ── Фото в IndexedDB ─────────────────────────────────────────────────────────
const DB_NAME = 'zoya-drafts'
const STORE = 'photos'

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB недоступна'))
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE) }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function withStore(mode, fn) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = fn(tx.objectStore(STORE))
    tx.oncomplete = () => { db.close(); resolve(req?.result) }
    tx.onerror = () => { db.close(); reject(tx.error) }
    tx.onabort = () => { db.close(); reject(tx.error) }
  }))
}

export function putPhoto(key, base64, mime) {
  return withStore('readwrite', s => s.put({ base64, mime, savedAt: Date.now() }, key))
}

export function getPhoto(key) {
  return withStore('readonly', s => s.get(key)).then(v => v || null)
}

export function delPhoto(key) {
  return withStore('readwrite', s => s.delete(key))
}
